import BadgeProductStatus from "../../components/BadgeProductStatus";
import ButtonPillBrand from "../../components/ButtonPillBrand";
import { numberWithCommas } from "../../helpers/formatter";
import { format } from "date-fns";

interface RequiredProps {
  info: any;
}

interface OptionalProps {
  /** ข้อมูลบรรจุภัณฑ์ (Bar Code / Hierachy / Package / Weight / Dimension) - tab PK / CS (Photo) ไม่ใช้ */
  showPackagingInfo?: boolean;
  /**
   * id ของกล่อง More Info ต้องไม่ซ้ำกันในหน้าเดียว
   * เพราะ InfoCard ถูก render ไว้ทุก tab พร้อมกัน (panel ซ่อนด้วย hidden ไม่ได้ unmount)
   * ถ้า id ซ้ำ tw-elements จะไปเปิดกล่องของ tab อื่นที่เจอตัวแรกแทน
   */
  collapseId?: string;
}

interface Props extends RequiredProps, OptionalProps {}

// ใช้ default parameter ไม่ใช่ defaultProps (react เตือนว่าจะถอด defaultProps ออกจาก function component)
function InfoCard({
  info,
  showPackagingInfo = true,
  collapseId = "collapseExample",
}: Props) {
  return (
    <>
      <div className="flex flex-row justify-start gap-2 mb-2">
        <ButtonPillBrand brand={info.brand} />
        <BadgeProductStatus status={info.status} />
      </div>
      <h2 className="mb-2 text-3xl font-medium leading-tight text-neutral-800 dark:text-neutral-50">
        {info.name ? info.name : "-"}
      </h2>

      <div className="my-4 text-lg">{`${info.titleTH}`}</div>

      <div className="my-4 text-lg">{`${info.titleEN}`}</div>

      <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
        <span className="text-lg font-bold text-neutral-600">Brand Type</span>
        <div className="flex flex-row justify-end">
          <span className="text-lg italic font-bold">{`${info.brandType}`}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
        <span className="text-lg font-bold text-neutral-600">Category</span>
        <div className="flex flex-row justify-end">
          <span className="text-lg">{`${
            info.category ? info.category : "-"
          }`}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
        <span className="text-lg font-bold text-neutral-600">Sub Category</span>
        <div className="flex flex-row justify-end">
          <span className="text-lg">{`${
            info.subcategory ? info.subcategory : "-"
          }`}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
        <span className="text-lg font-bold text-neutral-600">
          Material Code
        </span>
        <div className="flex flex-row justify-end">
          <span className="text-lg text-neutral-600 ">{`${info.materialCode}`}</span>
        </div>
      </div>

      {showPackagingInfo ? (
        <>
        <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
          <span className="text-lg font-bold text-neutral-600">Bar Code</span>
          <div className="flex flex-row justify-end">
            <span className="text-lg text-neutral-600 proportional-nums">{`${
              info.barcode ? info.barcode : "-"
            }`}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
          <span className="text-lg font-bold text-neutral-600">Hierachy</span>
          <div className="flex flex-row justify-end">
            <span className="text-lg text-neutral-600 proportional-nums">{`${
              info.productHierachy ? info.productHierachy : "-"
            }`}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
          <span className="text-lg font-bold text-neutral-600">Package</span>
          <div className="flex flex-row justify-end">
            <span className="text-lg font-bold text-neutral-600">{`${
              info.packaging ? info.packaging : "-"
            }`}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
          <span className="text-lg font-bold text-neutral-600">Weight ({info.weightUnit ? info.weightUnit : "G"})</span>
          <div className="flex flex-row justify-end">
            <span className="text-lg text-neutral-600">
              {`${info.weight ? numberWithCommas(info.weight) : "-"}`}{" "}
              {/* <span className="font-bold">{info.weightUnit}</span>{" "} */}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 p-2 rounded lg:grid-cols-2 hover:bg-slate-200">
          <div>
            <span className="text-lg font-bold text-neutral-600">Dimension (MM)</span>
          </div>

          <div className="flex flex-col">
            <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 sm:px-6 lg:px-8">
                <div className="overflow-hidden">
                  <table className="min-w-full text-sm font-light text-center">
                    <thead className="font-medium text-white border-b bg-neutral-800 dark:border-neutral-500 dark:bg-neutral-900">
                      <tr>
                        <th scope="col" className="px-6 py-4 ">
                          กว้าง
                        </th>
                        <th scope="col" className="px-6 py-4 ">
                          ยาว
                        </th>
                        <th scope="col" className="px-6 py-4 ">
                          สูง
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b dark:border-neutral-500">
                        <td className="px-6 py-4 font-bold whitespace-nowrap">
                          {info.width ? numberWithCommas(info.width) : "-"}
                        </td>
                        <td className="px-6 py-4 font-bold whitespace-nowrap ">
                          {info.length ? numberWithCommas(info.length) : "-"}
                        </td>
                        <td className="px-6 py-4 font-bold whitespace-nowrap ">
                          {info.height ? numberWithCommas(info.height) : "-"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
      ) : null}

      <a
        className="mt-2 inline-block rounded bg-primary px-6 pb-2 pt-2.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-primary-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)]"
        data-te-collapse-init
        data-te-ripple-init
        data-te-ripple-color="light"
        href={`#${collapseId}`}
        role="button"
        aria-expanded="false"
        aria-controls={collapseId}
      >
        More Info
      </a>

      <div
        className="!visible hidden"
        id={collapseId}
        data-te-collapse-item
      >
        <div className="block rounded-lg bg-white p-6 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)] dark:bg-neutral-700 dark:text-neutral-50">
          <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
            <span className="font-bold text-normal text-neutral-600 ">
              Classifier
            </span>
            <div>
              <div className="flex flex-row justify-end">
                <span>{info.classifier ? info.classifier : "-"}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
            <span className="font-bold text-normal text-neutral-600 ">
              Collection
            </span>
            <div className="flex flex-row justify-end">
              <span>{info.collection ? info.collection : "-"}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
            <span className="font-bold text-normal text-neutral-600 ">
              Color
            </span>
            <div className="flex flex-row justify-end">
              <span>{info.color ? info.color : "-"}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
            <span className="font-bold text-normal text-neutral-600 ">
              Function (s)
            </span>
            <div className="flex flex-wrap justify-end">
              <div>
                {info.functions ? (
                  <div className="flex flex-wrap justify-end gap-2">
                    {" "}
                    {info.functions.map((fc: string) => (
                      <span
                        key={`fc-${fc}`}
                        className="px-2 py-1 text-white bg-yellow-500 rounded shadow-lg"
                      >
                        {fc}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span> - </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
            <span className="font-bold text-normal text-neutral-600 ">
              Tag (s)
            </span>
            <div className="flex flex-wrap justify-end">
              <div>
                {info.tags ? (
                  <div className="flex flex-wrap justify-end gap-2">
                    {" "}
                    {info.tags.map((tag: string) => (
                      <span
                        key={`tag-${tag}`}
                        className="px-2 py-1 text-white rounded shadow-lg bg-sky-500"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span> - </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="grid grid-cols-1 p-1 rounded lg:grid-cols-2 hover:bg-slate-200">
          <span className="text-lg font-bold text-neutral-600">Created At</span>
          <div className="flex flex-row justify-end">
            <span className="text-lg italic text-green-600">{`${format(
              new Date(info.createdAt),
              "dd-MM-yyyy 'เวลา' h:mm a"
            )}`}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 p-1 mt-2 rounded lg:grid-cols-2 hover:bg-slate-200">
          <span className="text-lg font-bold text-neutral-600">Updated At</span>
          <div className="flex flex-row justify-end">
            <span className="text-lg italic text-blue-700">{`${format(
              new Date(info.updatedAt),
              "dd-MM-yyyy 'เวลา' h:mm a"
            )}`}</span>
          </div>
        </div>
      </div>
    </>
  );
}
export default InfoCard;
